/**
 * The single axios instance every API module goes through.
 *
 * It carries four cross-cutting concerns from `docs/API.md` §1 so no feature has
 * to remember them:
 *
 *  1. **Bearer token** on every request, and `X-Request-Id` so a failure in the
 *     browser can be matched to a row in `audit.system_log`.
 *  2. **Silent refresh.** A `401 token_expired` triggers one refresh and one
 *     retry. Refresh tokens rotate, so concurrent 401s must share a single
 *     refresh — firing several would present the same rotated token twice and
 *     the API would revoke the whole session as a suspected theft.
 *  3. **Error normalisation.** Every rejection becomes an `ApiError` with the
 *     `code` from the §1.9 envelope, because callers branch on `code` and never
 *     on the HTTP status or the message text.
 *  4. **Optimistic-locking headers** (`If-Match`) and `Idempotency-Key` pass
 *     straight through; helpers below build them.
 */
import axios from 'axios';
import { clearStoredSession, readStoredSession, writeStoredSession } from './tokenStore.js';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:4000/v1';

/**
 * A failed API call, in the shape the UI actually needs.
 *
 * `code` is the machine-readable string from `docs/API.md` §19 — the only field
 * that is safe to branch on. `details` names the offending fields on a
 * validation failure so a form can map them back onto inputs. `payload` keeps
 * everything else the envelope carried (`current_version` and `current` on a
 * stale write, `locked_until` on a lockout, `holders` on `role_in_use`).
 */
export class ApiError extends Error {
  constructor({ code, message, details = [], requestId, status, payload = {} }) {
    super(message || 'Something went wrong.');
    this.name = 'ApiError';
    this.code = code ?? 'unknown_error';
    this.details = details;
    this.requestId = requestId;
    this.status = status;
    this.payload = payload;
  }

  /** True when the server rejected specific fields rather than the whole request. */
  get isValidation() {
    return this.code === 'validation_failed' || this.details.length > 0;
  }

  /** Field name → message, ready for react-hook-form's `setError`. */
  fieldErrors() {
    const out = {};
    for (const d of this.details) {
      if (d.field) out[d.field] = d.message ?? d.code;
    }
    return out;
  }
}

export const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

/* -------------------------------------------------------------------------- */
/* Request: authorisation and correlation                                      */
/* -------------------------------------------------------------------------- */

apiClient.interceptors.request.use((config) => {
  const session = readStoredSession();
  if (session?.accessToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${session.accessToken}`;
  }
  // Generated client-side so a trace spans the browser and the API. The server
  // echoes it back and writes it into every audit and log row for the request.
  config.headers['X-Request-Id'] ??= crypto.randomUUID();
  return config;
});

/* -------------------------------------------------------------------------- */
/* Response: refresh-once, then normalise                                      */
/* -------------------------------------------------------------------------- */

/**
 * The in-flight refresh, shared by every request that 401s while it runs.
 *
 * Without this, three parallel requests expiring together would each POST the
 * same refresh token. The first rotates it; the second and third then present a
 * token the server has already spent, which it treats as a stolen-token replay
 * and answers by revoking the entire session. Sharing one promise is what keeps
 * a normal page load from logging the user out.
 */
let refreshInFlight = null;

/** Called when a session can no longer be recovered. Set by AuthContext. */
let onSessionLost = () => {};

/** @param {() => void} handler run when refresh fails and the user must sign in again */
export function setSessionLostHandler(handler) {
  onSessionLost = handler;
}

async function refreshSession() {
  const session = readStoredSession();
  if (!session?.refreshToken) throw new Error('no refresh token');

  // A bare axios call, not apiClient: the interceptor below must not try to
  // refresh a refresh.
  const { data } = await axios.post(
    `${BASE_URL}/auth/refresh`,
    { refresh_token: session.refreshToken },
    { headers: { 'Content-Type': 'application/json' } },
  );

  writeStoredSession({
    ...session,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + data.expires_in * 1000,
  });
  return data.access_token;
}

/** Turn any axios failure into an ApiError carrying the §1.9 envelope. */
function toApiError(error) {
  if (!error.response) {
    return new ApiError({
      code: error.code === 'ECONNABORTED' ? 'timeout' : 'network_error',
      message:
        error.code === 'ECONNABORTED'
          ? 'The server took too long to answer.'
          : 'Could not reach the server. Check that the API is running.',
      status: 0,
    });
  }

  const { status, data } = error.response;
  const envelope = data?.error ?? {};
  const { code, message, details, request_id: requestId, ...payload } = envelope;

  return new ApiError({
    code,
    message,
    details: details ?? [],
    requestId,
    status,
    payload,
  });
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const code = error.response?.data?.error?.code;

    const canRetry =
      status === 401 &&
      code === 'token_expired' &&
      original &&
      !original._retried &&
      !original.url?.includes('/auth/refresh');

    if (canRetry) {
      original._retried = true;
      try {
        refreshInFlight ??= refreshSession().finally(() => {
          refreshInFlight = null;
        });
        const accessToken = await refreshInFlight;
        original.headers.Authorization = `Bearer ${accessToken}`;
        return apiClient(original);
      } catch {
        clearStoredSession();
        onSessionLost();
        return Promise.reject(
          new ApiError({
            code: 'session_expired',
            message: 'Your session has ended. Please sign in again.',
            status: 401,
          }),
        );
      }
    }

    // session_revoked and refresh_token_invalid are terminal: the API has
    // already decided this session is over, and retrying cannot help.
    if (status === 401 && ['session_revoked', 'refresh_token_invalid', 'token_invalid'].includes(code)) {
      clearStoredSession();
      onSessionLost();
    }

    return Promise.reject(toApiError(error));
  },
);

/* -------------------------------------------------------------------------- */
/* Header helpers                                                              */
/* -------------------------------------------------------------------------- */

/**
 * `If-Match` for a conditional write (§1.7).
 * @param {number} rowVersion the version the user was editing
 */
export const ifMatch = (rowVersion) => ({ 'If-Match': `"${rowVersion}"` });

/**
 * A fresh `Idempotency-Key` (§1.8). Generate one per user intent — per click of
 * Save — and reuse it across retries of that same click, never across two
 * separate creates.
 */
export const idempotencyKey = () => ({ 'Idempotency-Key': crypto.randomUUID() });

export default apiClient;
